import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ControleProjetoCard } from "./ControleProjetoCard";
import { Clock, Play, CheckCircle, Anchor, AlertTriangle, RotateCcw } from "lucide-react";
import type { ControleProjeto } from "@/types/controle-projeto";
import type { EstadoProjeto } from "@/utils/controle-projeto-estado";

// Nº de cards renderizados por coluna antes do "Mostrar mais". Só entra em ação
// quando o histórico está ligado (coluna Finalizados tem ~2k cards).
const CARDS_POR_PAGINA = 24;

interface AcoesProjeto {
  onRenovar: (projeto: ControleProjeto) => void;
  onEditar: (projeto: ControleProjeto) => void;
  onExcluir: (projetoId: string) => void;
  onFinalizar: (projeto: ControleProjeto) => void;
  onConfirmarPlanejado: (projeto: ControleProjeto) => void;
}

interface ProjetoKanbanProps extends AcoesProjeto {
  projetosPorEstado: Partial<Record<EstadoProjeto, ControleProjeto[]>>;
  mostrarHistorico: boolean;
}

interface ColunaKanban {
  id: EstadoProjeto;
  title: string;
  icon: typeof Clock;
  color: string;
  historico?: boolean;
}

// Colunas de histórico (Renovados/Finalizados) só aparecem com o toggle ligado ou
// quando têm algum card (renovado ainda em curso carrega por padrão).
const COLUNAS: ColunaKanban[] = [
  { id: "a_iniciar", title: "A Iniciar", icon: Clock, color: "bg-yellow-500" },
  { id: "em_andamento", title: "Em Andamento", icon: Play, color: "bg-blue-500" },
  { id: "fixo", title: "Projetos Fixos", icon: Anchor, color: "bg-blue-600" },
  { id: "vencido", title: "Vencidos", icon: AlertTriangle, color: "bg-red-500" },
  { id: "renovado", title: "Renovados", icon: RotateCcw, color: "bg-violet-500", historico: true },
  { id: "finalizado", title: "Finalizados", icon: CheckCircle, color: "bg-green-500", historico: true },
];

// Corpo de uma coluna com renderização progressiva. `paginado` liga o limite +
// "Mostrar mais" (dados já em memória, é só fatiar).
function ColunaProjetos({
  projetos,
  paginado,
  ...acoes
}: AcoesProjeto & {
  projetos: ControleProjeto[];
  paginado: boolean;
}) {
  const [visiveis, setVisiveis] = useState(CARDS_POR_PAGINA);
  const exibidos = paginado ? projetos.slice(0, visiveis) : projetos;
  const restantes = projetos.length - exibidos.length;

  if (projetos.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 text-muted-foreground text-xs">
        Nenhum projeto neste status
      </div>
    );
  }

  return (
    <>
      {exibidos.map((projeto) => (
        <ControleProjetoCard key={projeto.id} projeto={projeto} {...acoes} />
      ))}
      {restantes > 0 && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            className="text-xs w-full"
            onClick={() => setVisiveis((atual) => atual + CARDS_POR_PAGINA)}
          >
            Mostrar mais ({restantes})
          </Button>
        </div>
      )}
    </>
  );
}

export function ProjetoKanban({ projetosPorEstado, mostrarHistorico, ...acoes }: ProjetoKanbanProps) {
  const colunasVisiveis = COLUNAS.filter(
    (coluna) => !coluna.historico || mostrarHistorico || (projetosPorEstado[coluna.id]?.length ?? 0) > 0,
  );

  return (
    // Scroll horizontal no mobile em vez de espremer as colunas: cada coluna
    // tem largura mínima e cresce (flex-1) pra preencher em telas largas.
    <div className="flex gap-4 h-full overflow-x-auto pb-2">
      {colunasVisiveis.map((coluna) => {
        const projetos = projetosPorEstado[coluna.id] || [];
        const Icon = coluna.icon;

        return (
          <Card key={coluna.id} className="flex flex-col h-full min-w-[260px] flex-1">
            <CardHeader className="pb-2 px-3 pt-3">
              <CardTitle className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span>{coluna.title}</span>
                </div>
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <div className={`w-2 h-2 rounded-full ${coluna.color}`} />
                  {projetos.length}
                </Badge>
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-1 overflow-y-auto space-y-2 px-3 pb-3">
              <ColunaProjetos projetos={projetos} paginado={mostrarHistorico} {...acoes} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
